import { Link, useNavigate, useLocation } from 'react-router-dom';
import { Search, Moon, Sun, User, LogOut, Sparkles, X } from 'lucide-react';
import { useState, useEffect } from 'react';
import { useThemeStore } from '../../store/useThemeStore';
import authService from '../../services/authService';
import { LINE_GRADIENT, FILL_GRADIENT } from '../../constants/gradients';
import Logo from './Logo';

interface NavbarProps {
  user?: any;
  onLogout: () => void;
}

export default function Navbar({ user, onLogout }: NavbarProps) {
  const navigate = useNavigate();
  const location = useLocation();
  const [searchQuery, setSearchQuery] = useState('');
  const [showSearch, setShowSearch] = useState(false);
  const { isDark, toggleTheme } = useThemeStore();
  const [showUserMenu, setShowUserMenu] = useState(false);
  const [isVisible, setIsVisible] = useState(true);
  const [lastScrollY, setLastScrollY] = useState(0);

  // Handle scroll to show/hide navbar
  useEffect(() => {
    const handleScroll = () => {
      const currentScrollY = window.scrollY;
      
      // Show navbar when scrolling up or at top
      if (currentScrollY < lastScrollY || currentScrollY < 10) {
        setIsVisible(true);
      } 
      // Hide navbar when scrolling down (only after scrolling past 100px)
      else if (currentScrollY > 100 && currentScrollY > lastScrollY) {
        setIsVisible(false);
      }
      
      setLastScrollY(currentScrollY);
    };

    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, [lastScrollY]);

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
    if (searchQuery.trim()) {
      navigate(`/search?q=${encodeURIComponent(searchQuery.trim())}`);
      setShowSearch(false);
      setSearchQuery('');
    }
  };
  
  // Determine active tab
  const isHomeActive = location.pathname === '/';
  const isMyGamesActive = location.pathname === '/my-games';
  const isCategoriesActive = location.pathname === '/categories';
  
  // Studio URL from environment
  const studioUrl = import.meta.env.VITE_STUDIO_URL || 'http://localhost:3002';

  return (
    <nav 
      className={`fixed top-0 left-0 right-0 z-50 bg-white dark:bg-dark-surface border-b border-gray-200 dark:border-dark-border shadow-sm transition-transform duration-300 ${
        isVisible ? 'translate-y-0' : '-translate-y-full'
      }`}
    >
      <div className="container mx-auto px-4">
        <div className="flex items-center justify-between h-16">
          {/* Left: Logo + Navigation Tabs */}
          <div className="flex items-center gap-12">
            {/* Logo */}
            <Logo size="md" showIcon={true} className="text-xl font-bold" />
            
            {/* Navigation Tabs */}
            <div className="flex items-stretch h-16 gap-6">
              <Link
                to="/"
                className={`relative px-2 flex items-center font-medium transition-colors ${
                  isHomeActive
                    ? 'text-gray-900 dark:text-white'
                    : 'text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white'
                }`}
              >
                Home
                {isHomeActive && (
                  <div 
                    className="absolute bottom-0 left-0 right-0 h-1"
                    style={{ background: LINE_GRADIENT }}
                  />
                )}
              </Link>
              <Link
                to="/my-games"
                className={`relative px-2 flex items-center font-medium transition-colors ${
                  isMyGamesActive
                    ? 'text-gray-900 dark:text-white'
                    : 'text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white'
                }`}
              >
                My Games
                {isMyGamesActive && (
                  <div 
                    className="absolute bottom-0 left-0 right-0 h-1"
                    style={{ background: LINE_GRADIENT }}
                  />
                )}
              </Link>
              <Link
                to="/categories"
                className={`relative px-2 flex items-center font-medium transition-colors ${
                  isCategoriesActive
                    ? 'text-gray-900 dark:text-white'
                    : 'text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white'
                }`}
              >
                Categories
                {isCategoriesActive && (
                  <div 
                    className="absolute bottom-0 left-0 right-0 h-1"
                    style={{ background: LINE_GRADIENT }}
                  />
                )}
              </Link>
            </div>
          </div>

          {/* Right: Search, Theme Toggle, Create Link, User Menu */}
          <div className="flex items-center gap-3">
            {/* Search - Collapsed/Expanded */}
            {showSearch ? (
              <form onSubmit={handleSearch} className="flex items-center gap-2">
                <div className="relative group">
                  <div 
                    className="absolute -inset-[2px] rounded-[10px] opacity-0 group-focus-within:opacity-100 transition-opacity pointer-events-none"
                    style={{ background: LINE_GRADIENT }}
                  />
                  <div className="relative flex items-center">
                    <Search className="absolute left-3 w-5 h-5 text-gray-400 dark:text-gray-500 pointer-events-none z-10" />
                    <input
                      type="text"
                      value={searchQuery}
                      onChange={(e) => setSearchQuery(e.target.value)}
                      placeholder="Search games..."
                      autoFocus
                      className="relative w-64 pl-10 pr-4 py-2 bg-gray-50 dark:bg-dark-bg border-2 border-gray-200 dark:border-dark-border rounded-lg text-gray-900 dark:text-dark-text placeholder-gray-400 dark:placeholder-gray-500 transition-all focus:outline-none focus:ring-0 focus:shadow-none"
                      style={{ outline: 'none', boxShadow: 'none' }}
                      onFocus={(e) => {
                        e.currentTarget.style.borderColor = 'transparent'
                        e.currentTarget.style.outline = 'none'
                        e.currentTarget.style.boxShadow = 'none'
                      }}
                      onBlur={(e) => {
                        e.currentTarget.style.borderColor = ''
                      }}
                    />
                  </div>
                </div>
                <button
                  type="button"
                  onClick={() => {
                    setShowSearch(false);
                    setSearchQuery('');
                  }}
                  className="p-2 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
                  aria-label="Close search"
                >
                  <X className="w-5 h-5 text-gray-600 dark:text-gray-300" />
                </button>
              </form>
            ) : (
              <button
                onClick={() => setShowSearch(true)}
                className="p-2 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
                aria-label="Search"
              >
                <Search className="w-5 h-5 text-gray-600 dark:text-gray-300" />
              </button>
            )}
            
            {/* Theme Toggle */}
            <button
              onClick={toggleTheme}
              className="p-2 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
              aria-label="Toggle theme"
            >
              {isDark ? (
                <Sun className="w-5 h-5 text-gray-600 dark:text-gray-300" />
              ) : (
                <Moon className="w-5 h-5 text-gray-600" />
              )}
            </button>

            {/* Create Link */}
            <a
              href={studioUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center gap-1.5 px-6 py-2 rounded-lg font-medium text-white shadow-md hover:shadow-lg hover:scale-[1.02] active:scale-[0.98] transition-all"
              style={{ background: FILL_GRADIENT }}
              title="Open Beemi Studio"
            >
              <Sparkles className="w-4 h-4" />
              <span>Create</span>
            </a>

            {/* User Menu */}
            {user ? (
              <div className="relative">
                <button
                  onClick={() => setShowUserMenu(!showUserMenu)}
                  className="flex items-center justify-center w-10 h-10 rounded-full bg-gradient-to-br from-[#FCB902] to-[#FF00CF] hover:opacity-90 transition-opacity"
                >
                  {user.image_url ? (
                    <img 
                      src={user.image_url} 
                      alt={user.display_name || user.username}
                      className="w-full h-full rounded-full object-cover"
                    />
                  ) : (
                    <User className="w-5 h-5 text-white" />
                  )}
                </button>

                {showUserMenu && (
                  <>
                    <div 
                      className="fixed inset-0 z-10" 
                      onClick={() => setShowUserMenu(false)}
                    />
                    <div className="absolute right-0 mt-2 w-48 bg-white dark:bg-dark-surface rounded-lg shadow-lg border border-gray-200 dark:border-dark-border z-20">
                      <div className="px-4 py-3 border-b border-gray-200 dark:border-dark-border">
                        <p className="text-sm font-semibold text-gray-900 dark:text-white">
                          {user.display_name || user.username}
                        </p>
                        {user.email && (
                          <p className="text-xs text-gray-500 dark:text-gray-400 truncate">
                            {user.email}
                          </p>
                        )}
                      </div>
                      <button
                        onClick={() => {
                          setShowUserMenu(false);
                          onLogout();
                        }}
                        className="flex items-center gap-2 w-full px-4 py-2 text-sm text-red-600 dark:text-red-400 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-b-lg"
                      >
                        <LogOut className="w-4 h-4" />
                        Logout
                      </button>
                    </div>
                  </>
                )}
              </div>
            ) : (
              <Link
                to="/login"
                className="btn-primary"
              >
                Login
              </Link>
            )}
          </div>
        </div>
      </div>
    </nav>
  );
}

